import { SpatialIndex, type SceneObject, type SceneReader } from '@os/engine';
import { isEffectivelyLocked, type SceneGraph } from './graph';
import type { ObjectData } from './schema';
import type { SceneStore } from './store';

/** World OBB from the graph (children are stored relative to their parent); `locked`: the effective lock when known. */
export const toSceneObject = (o: Readonly<ObjectData>, g: SceneGraph, locked = o.locked): SceneObject => {
  const w = g.world.get(o.id) ?? { x: o.x, y: o.y, w: o.w, h: o.h, rotation: o.rotation };
  return {
    id: o.id,
    type: o.type,
    obb: { cx: w.x + w.w / 2, cy: w.y + w.h / 2, w: w.w, h: w.h, rotation: w.rotation },
    z: o.z,
    rank: g.rank.get(o.id) ?? 0,
    parentId: g.parent.get(o.id) ?? null,
    locked,
    path: g.paths.get(o.id)?.samples,
  };
};

/**
 * The engine's read-only view of the store: OBB map and spatial index, rebuilt lazily after any change. Objects hidden
 * inside a collapsed section are left out. Selection is UI state, so the caller supplies it.
 */
export function createSceneReader(store: SceneStore, selection: () => readonly string[]): SceneReader & { destroy(): void } {
  let objects: Map<string, SceneObject> | null = null;
  const index = new SpatialIndex();
  const unsubscribe = store.subscribeList(() => void (objects = null));
  const fresh = () => {
    if (!objects) {
      const g = store.graph();
      const read = (id: string) => store.getObject(id);
      objects = new Map(store.list().filter((o) => !g.hidden.has(o.id)).map((o) => [o.id, toSceneObject(o, g, isEffectivelyLocked(g, read, o.id))]));
      index.load([...objects.values()]);
    }
    return objects;
  };
  return {
    objects: fresh,
    index: () => (fresh(), index),
    selection,
    destroy: unsubscribe,
  };
}
